import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { Camera, Loader2 } from 'lucide-react';

/**
 * My Profile — lets the signed-in user upload a profile picture.
 * The avatar is stored on the server and shown in the toolbar.
 */
export default function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const pickFile = () => fileRef.current && fileRef.current.click();

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
      toast.error('Please choose a PNG, JPG, WEBP or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5 MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await authAPI.uploadAvatar(preview);
      setPreview(null);
      await refreshUser();
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
    setBusy(false);
  };

  const currentSrc = user?.avatar_path ? fileUrl(user.avatar_path) : null;

  return (
    <div>
      <div className="toolbar"><h2>My Profile</h2></div>
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              {preview || currentSrc ? (
                <img
                  src={preview || currentSrc}
                  alt="Profile"
                  className="profile-avatar"
                  style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border)' }}
                />
              ) : (
                <div className="profile-avatar" style={{ width: 96, height: 96, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, fontWeight: 700 }}>
                  {((user?.first_name || user?.username || 'U')[0] || 'U').toUpperCase()}
                </div>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={pickFile}
                style={{ position: 'absolute', bottom: -6, right: -6, borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                title="Upload picture"
              >
                <Camera size={15} />
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={onFile} />
            </div>
            <div>
              <h3 style={{ margin: 0 }}>{user?.first_name} {user?.last_name}</h3>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>@{user?.username} · {user?.email}</div>
              <div style={{ marginTop: 8 }}>
                <span className="badge badge-posted">{user?.role_name || 'User'}</span>
              </div>
            </div>
          </div>

          {preview && (
            <div className="form-actions" style={{ marginTop: 18 }}>
              <button className="btn btn-primary" onClick={upload} disabled={busy}>
                {busy ? <Loader2 size={14} className="spin-slow" style={{ verticalAlign: '-2px', marginRight: 6 }} /> : null}
                {busy ? 'Uploading…' : 'Save Picture'}
              </button>
              <button className="btn btn-outline" onClick={() => setPreview(null)}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
